import { FC, useState, useRef, useEffect } from "react";

/**
 * International Phone Input Component
 * Supports country code dropdown with 10+ countries
 */

export interface CountryCode {
  code: string;
  country: string;
  flag: string;
  dialCode: string;
  phoneLength: number; // Expected phone length without country code
}

// Supported countries list (minimum 10 as per requirements)
export const COUNTRY_CODES: CountryCode[] = [
  { code: "IN", country: "India", flag: "🇮🇳", dialCode: "+91", phoneLength: 10 },
  { code: "US", country: "USA", flag: "🇺🇸", dialCode: "+1", phoneLength: 10 },
  { code: "CA", country: "Canada", flag: "🇨🇦", dialCode: "+1", phoneLength: 10 },
  { code: "GB", country: "UK", flag: "🇬🇧", dialCode: "+44", phoneLength: 10 },
  { code: "FR", country: "France", flag: "🇫🇷", dialCode: "+33", phoneLength: 9 },
  { code: "DE", country: "Germany", flag: "🇩🇪", dialCode: "+49", phoneLength: 11 },
  { code: "SG", country: "Singapore", flag: "🇸🇬", dialCode: "+65", phoneLength: 8 },
  { code: "AE", country: "UAE", flag: "🇦🇪", dialCode: "+971", phoneLength: 9 },
  { code: "QA", country: "Qatar", flag: "🇶🇦", dialCode: "+974", phoneLength: 8 },
  { code: "MY", country: "Malaysia", flag: "🇲🇾", dialCode: "+60", phoneLength: 10 },
  { code: "AU", country: "Australia", flag: "🇦🇺", dialCode: "+61", phoneLength: 9 },
  { code: "NZ", country: "New Zealand", flag: "🇳🇿", dialCode: "+64", phoneLength: 9 },
];

interface PhoneInputProps {
  label: string;
  countryCodeValue: string;
  phoneValue: string;
  onCountryCodeChange: (code: string) => void;
  onPhoneChange: (phone: string) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}

const PhoneInput: FC<PhoneInputProps> = ({
  label,
  countryCodeValue,
  phoneValue,
  onCountryCodeChange,
  onPhoneChange,
  placeholder = "Enter phone number",
  error,
  required = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Find selected country
  const selectedCountry = COUNTRY_CODES.find(
    (c) => c.dialCode === countryCodeValue
  ) || COUNTRY_CODES[0]; // Default to India

  // Filter countries based on search
  const filteredCountries = COUNTRY_CODES.filter(
    (c) =>
      c.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.dialCode.includes(searchTerm)
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCountrySelect = (country: CountryCode) => {
    onCountryCodeChange(country.dialCode);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits
    const value = e.target.value.replace(/\D/g, "");
    onPhoneChange(value);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="flex">
        {/* Country Code Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={`flex items-center gap-1 px-3 py-2.5 rounded-l-lg border border-r-0 border-neutral-300 bg-neutral-50 text-sm transition-all hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 ${
              disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <span className="text-base">{selectedCountry.flag}</span>
            <span className="font-medium text-neutral-700">
              {selectedCountry.dialCode}
            </span>
            <svg
              className={`w-4 h-4 text-neutral-500 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isOpen && (
            <div className="absolute z-50 mt-1 w-64 rounded-lg border border-neutral-200 bg-white shadow-lg">
              {/* Search Input */}
              <div className="p-2 border-b border-neutral-100">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search country..."
                  className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  autoFocus
                />
              </div>

              {/* Country List */}
              <div className="max-h-48 overflow-y-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountrySelect(country)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-neutral-50 transition-colors ${
                        selectedCountry.code === country.code
                          ? "bg-brand-primary/10"
                          : ""
                      }`}
                    >
                      <span className="text-lg">{country.flag}</span>
                      <span className="flex-1 text-left text-neutral-700">
                        {country.country}
                      </span>
                      <span className="text-neutral-500 font-medium">
                        {country.dialCode}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-4 text-sm text-neutral-500 text-center">
                    No countries found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <input
          type="tel"
          value={phoneValue}
          onChange={handlePhoneInput}
          maxLength={selectedCountry.phoneLength + 2} // Allow some buffer
          placeholder={placeholder}
          disabled={disabled}
          className={`flex-1 rounded-r-lg border border-neutral-300 px-4 py-2.5 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all ${
            error ? "border-red-500" : ""
          } ${disabled ? "bg-neutral-100 cursor-not-allowed" : ""}`}
        />
      </div>

      {/* Error Message */}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {/* Helper Text */}
      <p className="mt-1 text-xs text-neutral-400">
        {selectedCountry.phoneLength} digits expected for {selectedCountry.country}
      </p>
    </div>
  );
};

export default PhoneInput;
